// src/pages/integrations/CredentialsTab.jsx — SOUND-709
//
// Rebuilt on the house list pattern (RecordTable / ListSearchBar /
// ListPagination / PageKpis), the same components /mids and /tickets use.
//
// WHAT THE OLD ONE COULD NOT DO, and why that mattered:
//   - It never showed a PORTAL credential. Not a UI omission — GET /credentials
//     defaulted to `midId: null`, so 27 of 42 documents were excluded before any
//     component rendered (fixed in SOUND-707).
//   - It carried PROVIDER_PRESETS, a hardcoded map of three providers, while
//     nine were in use. That map is why `phoenix: ["apiKey","storeIds"]` treated
//     a store list — configuration — as a credential field.
//   - It had no password field, which is the entire reason 17 credential-shaped
//     values ended up in the free-text notes box, growing at about one every two
//     days.
import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "utilities/auth";
import { isInternal } from "utilities/userType";
import { useCredentials } from "hooks/useCredentials";
import { CREDENTIAL_KIND, statusToneFrom } from "utilities/statusTones";
import { hasSecretStorage } from "utilities/credentialFields";
import RecordTable from "components/Records/RecordTable";
import { parseSort } from "utilities/listSort";
import RecordStatusPill from "components/Records/RecordStatusPill";
import ListSearchBar from "components/Records/ListSearchBar";
import ListPagination from "components/Records/ListPagination";
import PageKpis from "components/PageKpis";
import RelativeTime from "components/RelativeTime";
import { KeyRound, Plus, Plug, Globe, ShieldAlert } from "lucide-react";
import CredentialCreateModal from "./CredentialCreateModal";

const kindTone = statusToneFrom(CREDENTIAL_KIND);

export default function CredentialsTab() {
  const { user } = useAuth();
  const isAdmin = isInternal(user);
  const navigate = useNavigate();

  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [sort, setSort] = useState("-createdAt");
  const [showCreate, setShowCreate] = useState(false);

  const { credentials, summary, pagination, loading, error, featureGated, refetch } =
    useCredentials({ page, limit, q, sortBy: parseSort(sort).field, sortDir: parseSort(sort).dir });

  const columns = useMemo(
    () => [
      {
        key: "label",
        width: "24%",
        header: "Credential",
        sortKey: "label",
        cell: (c) => <div className="truncate font-medium">{c.label || "—"}</div>,
      },
      {
        key: "provider",
        width: "12%",
        header: "Provider",
        sortKey: "provider",
        cell: (c) => <span className="capitalize">{c.provider || "—"}</span>,
      },
      {
        key: "kind",
        width: "10%",
        header: "Type",
        sortKey: "credentialType",
        cell: (c) => <RecordStatusPill status={c.credentialType} toneFor={kindTone} />,
      },
      // Internal-only, like MidPortfolio gates Source from processors: which
      // company OWNS a credential is the permission anchor, and a client has
      // exactly one so the column is noise for them.
      isAdmin && {
        key: "owner",
        width: "16%",
        header: "Owner",
        // SOUND-709 — the owner is a JOIN, not a stored field, so sorting it
        // needs the server to order by the joined name. It does
        // (sortBy=companyName runs an aggregation), which is why this column
        // carries a sortKey while /mids' Client column deliberately does not.
        sortKey: "companyName",
        cell: (c) => <div className="truncate">{c.companyName || c.companySlug || "—"}</div>,
      },
      {
        key: "storage",
        width: "14%",
        header: "Secret storage",
        // THE COLUMN THIS PAGE EXISTS FOR. A credential with nowhere to put a
        // secret is the reason passwords ended up in notes. Naming it in the
        // list makes the gap countable instead of anecdotal.
        cell: (c) =>
          hasSecretStorage(c) ? (
            <span className="text-xs text-muted-foreground">
              {Object.keys(c.vault.fields).join(", ")}
            </span>
          ) : (
            <span className="badge badge-warning" title="No secret storage — the password has nowhere to live">
              None
            </span>
          ),
      },
      {
        key: "lastAccess",
        width: "12%",
        header: "Last used",
        // Answers what the old system could not answer at all: is anything still
        // using this? That is what makes a revoke safe rather than hopeful, and
        // what offboarding needs — sophro and purelynutra still have live
        // credentials today.
        cell: (c) =>
          c.lastAccess?.at ? (
            <span className="text-xs text-muted-foreground">
              <RelativeTime value={c.lastAccess.at} /> · {c.lastAccess.consumer}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">Never</span>
          ),
      },
      {
        key: "updatedAt",
        width: "12%",
        header: "Updated",
        sortKey: "updatedAt",
        cell: (c) => <RelativeTime value={c.updatedAt} />,
      },
    ],
    [isAdmin]
  );

  if (featureGated) {
    return (
      <div className="rounded-lg border border-border bg-card p-8 text-center">
        <p className="text-sm text-muted-foreground">
          Credentials are not enabled for this account.
        </p>
      </div>
    );
  }

  // PageKpis renders its icon chip whether or not an icon is supplied, so an
  // item without one shows an empty circle — which is what all four of these
  // were doing. The contract is an ELEMENT (`<Icon />`), not a component:
  // PageKpis clones it to apply `h-5 w-5`, so passing `Icon` renders nothing.
  //
  // Icons follow the two kinds of credential this page exists to separate — a
  // machine connection versus a human logging into a website — because "API"
  // and "Portal" are the distinction the whole SOUND-709 rebuild is about.
  const noSecretStorage = summary.noSecretStorage ?? 0;
  const kpis = [
    {
      label: "Credentials",
      value: pagination.total ?? 0,
      tone: "brand",
      loading,
      icon: <KeyRound />,
    },
    {
      label: "API",
      value: summary.typeCounts?.api ?? 0,
      tone: "info",
      loading,
      icon: <Plug />,
    },
    {
      label: "Portal",
      value: summary.typeCounts?.portal ?? 0,
      tone: "info",
      loading,
      icon: <Globe />,
    },
    {
      // Tone is conditional on purpose: zero is the healthy state and should not
      // sit there amber implying an outstanding problem.
      label: "No secret storage",
      value: noSecretStorage,
      tone: noSecretStorage > 0 ? "warning" : "neutral",
      loading,
      icon: <ShieldAlert />,
    },
  ];

  return (
    <div className="space-y-4">
      <PageKpis items={kpis} />

      <ListSearchBar
        value={q}
        onChange={(v) => {
          setQ(v);
          setPage(1); // a new search must land on page 1, or the result set and
                      // the page number disagree and the list reads as empty
        }}
        placeholder="Search by client, label, provider or username"
      >
        {isAdmin && (
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
            <Plus className="mr-1 h-4 w-4" /> Add credential
          </button>
        )}
      </ListSearchBar>

      <RecordTable
        columns={columns}
        rows={credentials}
        rowKey={(c) => c.credentialId || c._id}
        // A row with no credentialId is a credential the A1 migration has not
        // reached — against an unmigrated database that is EVERY row, and the
        // old code navigated to /credentials/undefined. Refusing the click is
        // the honest response: there is no detail page for a record that has no
        // stable id yet.
        onRowClick={(c) => c.credentialId && navigate(`/credentials/${c.credentialId}`)}
        rowClassName={(c) => (c.credentialId ? "" : "opacity-60")}
        loading={loading}
        error={error}
        onRetry={refetch}
        filtered={Boolean(q)}
        sort={sort}
        onSortChange={(next) => { setSort(next); setPage(1); }}
        empty={{
          icon: KeyRound,
          // EmptyState renders `action` as a NODE, not { label, onClick } —
          // same contract as `icon` taking a component, not an element.
          action: isAdmin ? (
            <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
              <Plus className="mr-1 h-4 w-4" /> Add credential
            </button>
          ) : undefined,
          heading: "No credentials yet",
          description: "API and portal credentials will appear here once added.",
          errorText: "Failed to load credentials.",
        }}
        noMatch={{ heading: "No matching credentials" }}
      />

      <ListPagination
        page={pagination.page}
        limit={pagination.limit}
        total={pagination.total}
        totalPages={pagination.totalPages}
        onPageChange={setPage}
        onLimitChange={(l) => {
          setLimit(l);
          setPage(1);
        }}
      />

      {showCreate && (
        <CredentialCreateModal
          // SOUND-709 — deliberately NOT passing a companyId. This is the global
          // surface; the client is a CHOICE here. Passing the admin context
          // locked it to sound-admin, which owns 0 MIDs and 0 DBAs, so the DBA
          // and MID selectors rendered permanently empty and looked broken.
          onClose={() => setShowCreate(false)}
          onCreated={refetch}
        />
      )}
    </div>
  );
}
